import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import { serviceAreas, serviceAreaIntro } from "@/data/site";
import { Reveal } from "./motion";

const marketPaths = {
  "Dallas–Fort Worth Metroplex": "/dallas-fort-worth",
  "Memphis Metropolitan Area": "/memphis",
};

export const ServiceArea = () => {
  return (
    <section
      id="area"
      data-testid="service-area-section"
      className="relative z-10 overflow-hidden bg-[#1C1C1C]"
    >
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 pt-24 sm:pt-32">
        <Reveal className="max-w-3xl">
          <p className="text-xs tracking-[0.3em] uppercase text-[#FF4500] font-bold mb-4">
            Where We Serve
          </p>
          <h2 className="font-display font-bold uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
            Moving help where you need it
          </h2>
          <p className="mt-6 text-neutral-300 text-base sm:text-lg leading-relaxed">
            {serviceAreaIntro}
          </p>
        </Reveal>

        {/* Market blocks */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {serviceAreas.map((market, i) => (
            <Reveal key={market.region} delay={i * 0.08}>
              <div
                data-testid={`market-${i}`}
                className="h-full border border-[#3d3d3d] bg-[#262626]/85 backdrop-blur-md p-8 sm:p-10"
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#FF4500]" />
                  <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">
                    {market.region}
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {market.cities.map((c) => (
                    <span
                      key={c}
                      data-testid={`city-${c.toLowerCase().replace(/\s/g, "-")}`}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-100 bg-[#303030] border border-[#3d3d3d] px-3.5 py-2"
                    >
                      <MapPin className="h-3.5 w-3.5 text-[#FF4500]" strokeWidth={2} />
                      {c}
                    </span>
                  ))}
                </div>
                <p className="mt-6 text-sm text-neutral-300">
                  …and surrounding communities.
                </p>
                {marketPaths[market.region] && (
                  <Link
                    to={marketPaths[market.region]}
                    data-testid={`market-link-${marketPaths[market.region].slice(1)}`}
                    className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#FF4500] hover:text-white transition-colors"
                  >
                    Labor-only movers in {market.region}
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        {/* Additional locations / lead capture */}
        <Reveal delay={0.1}>
          <div
            data-testid="additional-locations"
            className="mt-6 border border-dashed border-[#454545] bg-[#1C1C1C]/85 backdrop-blur-md p-8 sm:p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6"
          >
            <div className="max-w-xl">
              <p className="text-xs tracking-[0.3em] uppercase text-neutral-400 font-bold mb-3">
                Additional Locations
              </p>
              <h3 className="font-display font-bold text-2xl tracking-tight">
                Moving somewhere else? We may be able to help.
              </h3>
              <p className="mt-3 text-neutral-400 leading-relaxed">
                Planning a move in another city? Tell us where you're located and
                we'll confirm availability. Service availability may vary by
                location and job requirements.
              </p>
            </div>
            <a
              href="#quote"
              data-testid="additional-locations-cta"
              className="group inline-flex items-center justify-center gap-2 bg-[#FF4500] text-[#1C1C1C] font-bold px-8 py-4 text-base hover:bg-[#E03C00] transition-colors whitespace-nowrap"
            >
              Request a Quote
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
